import React, { useMemo } from "react";
import { Alert, Card, CardContent, Typography } from "@mui/material";
import { LineChart } from "@mui/x-charts/LineChart";
import dayjs from "dayjs";

const MAX_DAILY_POINTS = 60;

function playTimeMs(row) {
    return Number(row.startedAtMs) || Number(row.createdAtMs) || Number(row.finishedAtMs) || 0;
}

function buildTimeline(rows) {
    const timed = (Array.isArray(rows) ? rows : [])
        .map((row) => ({ row, ms: playTimeMs(row) }))
        .filter((item) => item.ms > 0)
        .sort((a, b) => a.ms - b.ms);
    if (timed.length === 0) {
        return null;
    }

    const first = dayjs(timed[0].ms).startOf("day");
    const last = dayjs(timed[timed.length - 1].ms).startOf("day");
    const daySpan = Math.max(1, last.diff(first, "day") + 1);
    const weekly = daySpan > MAX_DAILY_POINTS;
    const step = weekly ? "week" : "day";
    const bucketStart = weekly ? first.startOf("week") : first;

    const counts = new Map();
    for (const item of timed) {
        const key = dayjs(item.ms).startOf(step).format("YYYY-MM-DD");
        counts.set(key, (counts.get(key) || 0) + 1);
    }

    const labels = [];
    const puzzles = [];
    const cumulative = [];
    let running = 0;
    let cursor = bucketStart;
    const end = weekly ? last.startOf("week") : last;
    while (!cursor.isAfter(end)) {
        const key = cursor.format("YYYY-MM-DD");
        const n = counts.get(key) || 0;
        running += n;
        labels.push(weekly ? cursor.format("MMM D") : cursor.format("MMM D"));
        puzzles.push(n);
        cumulative.push(running);
        cursor = cursor.add(1, step);
    }

    return { labels, puzzles, cumulative, weekly, total: timed.length };
}

export default function WordWheelPlayTimelineChart({ rows }) {
    const timeline = useMemo(() => buildTimeline(rows), [rows]);

    if (!timeline) {
        return <Alert severity="info">No timed plays to chart for this player.</Alert>;
    }

    return (
        <Card>
            <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Puzzle timeline
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    X is {timeline.weekly ? "week" : "day"}; Y is puzzles played.
                    {timeline.weekly
                        ? " Plays are grouped by week because the range is longer than two months."
                        : ""}
                    {" "}
                    {timeline.total} puzzle{timeline.total === 1 ? "" : "s"} total.
                </Typography>
                <LineChart
                    height={360}
                    xAxis={[{ scaleType: "point", data: timeline.labels, label: "Time" }]}
                    yAxis={[{ min: 0, label: "Puzzles" }]}
                    series={[
                        {
                            data: timeline.puzzles,
                            label: "Puzzles",
                            color: "#1976d2",
                            showMark: timeline.labels.length <= 40,
                        },
                        {
                            data: timeline.cumulative,
                            label: "Cumulative",
                            color: "#3BA666",
                            showMark: false,
                        },
                    ]}
                />
            </CardContent>
        </Card>
    );
}
